<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: RecentPersonCard                                      |
| Section: atoms                                                   |
| Purpose: Person tile for the mixed recent-changes grid.          |
+------------------------------------------------------------------+
-->
<template>
    <a class="recent-person-card" :href="isPrivate ? privateLoginHref : href" @click="handleClick"
        @mouseenter="applyRandomCardHover" @mouseleave="resetRandomCardHover">
        <div class="recent-person-card_visual" :style="{ transform: hoverTransform }">
            <span v-if="typeLabel" class="recent-person-card_type">{{ typeLabel }}</span>
            <div v-if="!isPrivate" class="recent-person-card_photo"
                :style="{ backgroundImage: photoBackgroundImage }"></div>
            <img v-else class="recent-person-card_lock" src="@/assets/icons/lock_black.svg" alt="" />
        </div>

        <p class="recent-person-card_summary">
            <span v-if="!isPrivate" class="recent-person-card_name">{{ name }}</span>
            <span v-if="!isPrivate" class="recent-person-card_years">
                <img :src="genderIconSrc" :alt="gender" />
                {{ years }}
            </span>
            <span v-else class="recent-person-card_private">
                {{ placeholder ? $t('privacy.livingPlaceholder') : $t('nav.privacyHidden') }}
            </span>
            <span v-if="relativeDate" class="recent-person-card_date recent-person-card_date--mobile">
                {{ relativeDate }}
            </span>
        </p>
        <p v-if="relativeDate" class="recent-person-card_date recent-person-card_date--desktop">
            {{ relativeDate }}
        </p>
    </a>
</template>

<script>
import { getDefaultPhoto } from '@/utils/photoUtils';
import { useAuth } from '@/composables/useAuth';
import { useLocale } from '@/composables/useLocale';
import { useRandomCardHover } from '@/composables/useRandomCardHover';
import { isLivingPlaceholder, isPrivateLivingPerson } from '@/utils/personPrivacy';
import { getBackgroundImageUrl } from '@/utils/mediaUtils';

export default {
    name: 'RecentPersonCard',
    setup() {
        const { authToken } = useAuth();
        const { localePath } = useLocale();
        const randomCardHover = useRandomCardHover();

        return {
            authToken,
            localePath,
            ...randomCardHover,
        };
    },
    props: {
        photoSrc: {
            type: String,
            default: '',
        },
        name: {
            type: String,
            required: true,
        },
        years: {
            type: String,
            required: true,
        },
        gender: {
            type: String,
            required: true,
            validator(value) {
                return ['male', 'female', 'M', 'F'].includes(value);
            },
        },
        href: {
            type: String,
            required: true,
        },
        relativeDate: {
            type: String,
            default: '',
        },
        typeLabel: {
            type: String,
            default: '',
        },
        living: {
            type: Boolean,
            required: true,
        },
        placeholder: {
            type: Boolean,
            default: false,
        },
        personID: {
            type: [String, Number],
            default: '',
        },
        firstname: {
            type: String,
            default: '',
        },
        lastname: {
            type: String,
            default: '',
        },
    },
    computed: {
        privacyPerson() {
            return {
                personID: this.personID,
                firstname: this.firstname,
                lastname: this.lastname,
                name: this.name,
                living: this.living,
                placeholder: this.placeholder,
                privacyStatus: this.placeholder ? 'living' : undefined,
            };
        },
        isPrivate() {
            return isLivingPlaceholder(this.privacyPerson)
                || isPrivateLivingPerson(this.privacyPerson, this.authToken);
        },
        privateLoginHref() {
            if (this.placeholder) return this.localePath('/login');
            return this.$router.resolve({
                path: this.localePath('/login'),
                query: { redirect: this.href },
            }).href;
        },
        displayPhotoSrc() {
            return this.photoSrc || getDefaultPhoto(this.gender);
        },
        photoBackgroundImage() {
            return getBackgroundImageUrl(this.displayPhotoSrc);
        },
        genderIconSrc() {
            const mappedGender = this.gender === 'M' ? 'male' : this.gender === 'F' ? 'female' : this.gender;
            return mappedGender === 'male'
                ? require('@/assets/icons/male.svg')
                : require('@/assets/icons/female.svg');
        },
    },
    methods: {
        handleClick(event) {
            if (!this.isPrivate) return;

            event.preventDefault();
            if (this.placeholder) {
                this.$router.push(this.localePath('/login'));
                return;
            }
            this.$router.push({
                path: this.localePath('/login'),
                query: { redirect: this.href },
            });
        },
    },
};
</script>

<style scoped>
.recent-person-card {
    display: flex;
    flex-direction: column;
    align-self: stretch;
    align-items: flex-start;
    gap: 16px;
    min-width: 0;
    height: 100%;
    color: var(--brand-text-dark);
    text-decoration: none;
    cursor: pointer;
    transition: var(--transition-bounce);
}

.recent-person-card_visual {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    width: 100%;
    aspect-ratio: 1.5;
    padding: 24px;
    border-radius: var(--border-radius-xl);
    background: var(--brand-bg-1);
    box-shadow: 0 0 0 0 var(--brand-bg-1), var(--shadow-card-sm);
    transition: var(--transition-bounce);
}

.recent-person-card_photo {
    width: min(96px, 52%);
    aspect-ratio: 1;
    border-radius: var(--border-radius-pill);
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    box-shadow: var(--shadow-photo);
}

.recent-person-card_type {
    position: absolute;
    z-index: 1;
    top: 10px;
    left: 10px;
    padding: 4px 8px;
    border-radius: var(--border-radius-pill);
    background: rgba(255, 255, 255, .9);
    color: var(--brand-text-dark);
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    box-shadow: var(--shadow-card-sm);
}

.recent-person-card_lock {
    width: 20px;
    height: 20px;
}

.recent-person-card_summary {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    min-width: 0;
    font-weight: 600;
    color: var(--brand-text-dark);
}

.recent-person-card_name {
    display: -webkit-box;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
}

.recent-person-card:hover .recent-person-card_name,
.recent-person-card:hover .recent-person-card_private {
    text-decoration: underline;
}

.recent-person-card_years {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 400;
}

.recent-person-card_years img {
    width: 12px;
    flex: 0 0 auto;
}

.recent-person-card_private {
    font-weight: 600;
}

.recent-person-card_date {
    color: var(--brand-text-dark);
    opacity: .6;
    font-size: 14px;
    font-weight: 400;
}

.recent-person-card_date--mobile {
    display: none;
}

.recent-person-card_date--desktop {
    margin-top: auto;
}

@media (max-width: 860px) {
    .recent-person-card {
        flex-direction: row;
    }

    .recent-person-card_visual {
        width: 28%;
        flex: 0 0 28%;
        aspect-ratio: auto;
        min-height: 0;
        height: auto;
        padding: 12px;
    }

    .recent-person-card_photo {
        width: min(72px, 58%);
    }

    .recent-person-card_summary {
        flex: 1 1 auto;
        align-self: stretch;
        justify-content: flex-start;
    }

    .recent-person-card_date--desktop {
        display: none;
    }

    .recent-person-card_date--mobile {
        display: block;
        margin-top: auto;
        opacity: .6;
    }
}
</style>
